import { useState, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { RootState } from "../../../redux/store";
import {
  selectCrmLeadsFilters,
  setCrmLeadsDealStage,
  setCrmLeadsDealPropertyName,
  setCrmLeadsDealRoomType,
  setCrmLeadsDealAcademicYear,
  setCrmLeadsDealAvailabilityTitle,
  setCrmLeadsDealCity,
  setCrmLeadsDealAssignee,
} from "../../../redux/slices/filterSlice";
import {
  useGetDealStagesQuery,
  useGetDealDistinctCitiesQuery,
  useGetDealDistinctAvailabilityTitlesQuery,
  useGetDealDistinctAcademicYearsQuery,
  useGetDealDistinctRoomTypesQuery,
} from "../../../redux/services/dealsApi";
import { useGetAllAgentsQuery } from "../../../redux/services/userManagementApi";
import { useGetPropertiesListQuery } from "../../../redux/services/playgroundApi";

export type DealMultiSelectFieldKey =
  | "deal_stage"
  | "deal_property_name"
  | "deal_room_type"
  | "deal_academic_year"
  | "deal_availability_title"
  | "deal_city"
  | "deal_assignee";

type DealOption = { value: string; label: string };

interface DealMultiSelectFilterProps {
  fieldKey: DealMultiSelectFieldKey;
  onClose: () => void;
}

const SLICE_KEY_BY_FIELD: Record<
  DealMultiSelectFieldKey,
  | "dealStage"
  | "dealPropertyName"
  | "dealRoomType"
  | "dealAcademicYear"
  | "dealAvailabilityTitle"
  | "dealCity"
  | "dealAssignee"
> = {
  deal_stage: "dealStage",
  deal_property_name: "dealPropertyName",
  deal_room_type: "dealRoomType",
  deal_academic_year: "dealAcademicYear",
  deal_availability_title: "dealAvailabilityTitle",
  deal_city: "dealCity",
  deal_assignee: "dealAssignee",
};

const SET_ACTION_BY_FIELD = {
  deal_stage: setCrmLeadsDealStage,
  deal_property_name: setCrmLeadsDealPropertyName,
  deal_room_type: setCrmLeadsDealRoomType,
  deal_academic_year: setCrmLeadsDealAcademicYear,
  deal_availability_title: setCrmLeadsDealAvailabilityTitle,
  deal_city: setCrmLeadsDealCity,
  deal_assignee: setCrmLeadsDealAssignee,
} as const;

const SEARCH_PLACEHOLDER_BY_FIELD: Record<DealMultiSelectFieldKey, string> = {
  deal_stage: "Search Booking Stage",
  deal_property_name: "Search Property",
  deal_room_type: "Search Room Type",
  deal_academic_year: "Search Academic Year",
  deal_availability_title: "Search Availability Title",
  deal_city: "Search City",
  deal_assignee: "Search Booking Owner",
};

const DealMultiSelectFilter = ({
  fieldKey,
  onClose,
}: DealMultiSelectFilterProps) => {
  const dispatch = useDispatch();
  const crmLeadsFilters = useSelector(selectCrmLeadsFilters);
  const currentClientCode = useSelector(
    (state: RootState) => state?.auth?.user?.current_client_code
  );

  const selectedFilter = crmLeadsFilters[SLICE_KEY_BY_FIELD[fieldKey]];

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(
    Array.isArray(selectedFilter?.value) ? [...selectedFilter.value] : []
  );
  const [filteredOptions, setFilteredOptions] = useState<DealOption[]>([]);

  const { data: dealStagesData, isLoading: isStagesLoading } =
    useGetDealStagesQuery(undefined, { skip: fieldKey !== "deal_stage" });
  const { data: dealCitiesData, isLoading: isCitiesLoading } =
    useGetDealDistinctCitiesQuery(undefined, { skip: fieldKey !== "deal_city" });
  const {
    data: dealAvailabilityTitlesData,
    isLoading: isAvailabilityTitlesLoading,
  } = useGetDealDistinctAvailabilityTitlesQuery(undefined, {
    skip: fieldKey !== "deal_availability_title",
  });
  const { data: dealAcademicYearsData, isLoading: isAcademicYearsLoading } =
    useGetDealDistinctAcademicYearsQuery(undefined, {
      skip: fieldKey !== "deal_academic_year",
    });
  const { data: dealRoomTypesData, isLoading: isRoomTypesLoading } =
    useGetDealDistinctRoomTypesQuery(undefined, {
      skip: fieldKey !== "deal_room_type",
    });
  const { data: agentsData, isLoading: isAgentsLoading } = useGetAllAgentsQuery(
    undefined,
    { skip: fieldKey !== "deal_assignee" }
  );
  const { data: propertiesData, isLoading: isPropertiesLoading } =
    useGetPropertiesListQuery(
      { managerId: currentClientCode ?? "", page: 1, pageSize: 1000 },
      { skip: fieldKey !== "deal_property_name" || !currentClientCode }
    );

  const options = useMemo<DealOption[]>(() => {
    if (fieldKey === "deal_stage") {
      if (!Array.isArray(dealStagesData)) return [];
      return dealStagesData
        .map((stage) => {
          const stageKey = stage?.stage_key ?? stage?.key ?? "";
          return {
            value: stageKey,
            label: stage?.stage_title ?? stage?.title ?? stageKey,
          };
        })
        .filter((option) => option.value);
    }
    if (fieldKey === "deal_property_name") {
      return (
        propertiesData?.res?.map((property: { id: number; name: string }) => ({
          value: property.name,
          label: property.name,
        })) ?? []
      );
    }
    if (fieldKey === "deal_assignee") {
      return (
        agentsData?.items?.map(
          (agent: { _id: string; name: string; email: string }) => ({
            value: agent._id || agent.email,
            label: agent.name || agent.email,
          })
        ) ?? []
      );
    }
    const distinctValues =
      fieldKey === "deal_city"
        ? dealCitiesData?.values
        : fieldKey === "deal_availability_title"
          ? dealAvailabilityTitlesData?.values
          : fieldKey === "deal_academic_year"
            ? dealAcademicYearsData?.values
            : dealRoomTypesData?.values;
    return (distinctValues ?? []).map((value) => ({ value, label: value }));
  }, [
    fieldKey,
    dealStagesData,
    propertiesData,
    agentsData,
    dealCitiesData,
    dealAvailabilityTitlesData,
    dealAcademicYearsData,
    dealRoomTypesData,
  ]);

  const isLoading =
    isStagesLoading ||
    isCitiesLoading ||
    isAvailabilityTitlesLoading ||
    isAcademicYearsLoading ||
    isRoomTypesLoading ||
    isAgentsLoading ||
    isPropertiesLoading;

  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredOptions(options);
        return;
      }
      setFilteredOptions(
        options.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        )
      );
    },
    [options]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => handleSearch(searchQuery), 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const handleToggle = (optionValue: string) => {
    setSelectedIds((prev) =>
      prev.includes(optionValue)
        ? prev.filter((id) => id !== optionValue)
        : [...prev, optionValue]
    );
  };

  const handleApply = () => {
    const valueToLabel = new Map(
      options.map((option) => [option.value, option.label])
    );
    const displayValues = selectedIds.map(
      (id) => valueToLabel.get(id) ?? id
    );
    dispatch(
      SET_ACTION_BY_FIELD[fieldKey]({
        value: selectedIds,
        displayValue: displayValues,
      })
    );
    onClose();
  };

  const isApplyDisabled =
    JSON.stringify([...selectedIds].sort()) ===
    JSON.stringify([...((selectedFilter?.value as string[]) || [])].sort());

  return (
    <div className="flex flex-col h-[92vh] overflow-hidden">
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={SEARCH_PLACEHOLDER_BY_FIELD[fieldKey]}
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {selectedIds.length > 0 && (
        <div className="shrink-0">
          <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm px-3 py-1 block">
            SELECTED ({selectedIds.length})
          </span>
          <div className="max-h-[200px] overflow-y-auto">
            <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk">
              {selectedIds.map((optionValue) => (
                <div
                  key={optionValue}
                  onClick={() => handleToggle(optionValue)}
                  className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer"
                >
                  <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                    {options.find((option) => option.value === optionValue)
                      ?.label ?? optionValue}
                  </span>
                  <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                    <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col flex-1 min-h-0">
        <div className="shrink-0 px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Options
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                Loading...
              </div>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim() ? "No results found" : "No options available"}
              </div>
            </div>
          ) : (
            filteredOptions
              .filter((option) => !selectedIds.includes(option.value))
              .map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleToggle(option.value)}
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                      {option.label}
                    </div>
                  </div>
                  <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                    <div className="w-3 h-3 rounded-sm bg-transparent" />
                  </div>
                </div>
              ))
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default DealMultiSelectFilter;
